import React, { useState, useEffect, useRef } from 'react';
import { X, Check } from 'lucide-react';
import { getOverlayByAddressCoordinates } from '../../../../services/getAddressList';
import { saveFile, saveScreenshots } from '../../../../services/saveScreenshot';
import {
  cacheOverlaysScreenshots,
  getCacheOverlaysScreenshots,
} from '../../../../helper/cacheHelper';
import { ArcgisMap } from '../../mapComponent';
import {
  ButtonContainer,
  CaptureButton,
  CheckIcon,
  CloseButton,
  CompleteButton,
  CompletedItem,
  CompletedItemText,
  CompletedList,
  CurrentOverlaySection,
  ErrorMessage,
  MapContainer,
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  OverlayLabel,
  OverlayName,
  ProgressBar,
  ProgressFill,
  ProgressSection,
  ProgressText,
  Sidebar,
} from './modal.style';
import ModalLoadingScreen from '../../../../components/loadingScreen/modalLoading';

const NewMultipleScreenshotModal = ({
  isOpen,
  onClose,
  searchAddress,
  overlayOptions = [],
  onComplete,
  geoJsonCode,
}) => {
  const [currentOverlayData, setCurrentOverlayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completedScreenshots, setCompletedScreenshots] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCacheLoaded, setIsCacheLoaded] = useState(false);

  // ref to function provided by ArcgisMap
  const captureScreenshotFn = useRef(null);

  // ref to know component is mounted (to avoid state updates after unmount)
  const mountedRef = useRef(true);

  // Load cached screenshots
  useEffect(() => {
    mountedRef.current = true;
    if (isOpen) {
      console.debug('[MSModal] isOpen true -> loading cache');
      setIsCacheLoaded(false);
      const cached = getCacheOverlaysScreenshots();

      if (cached && Array.isArray(cached)) {
        const validCached = cached.filter(
          (cachedItem) =>
            cachedItem.screenshot &&
            overlayOptions.some((opt) => opt.overlayName === cachedItem.name)
        );
        setCompletedScreenshots(validCached);
        console.debug('[MSModal] cached screenshots loaded', validCached);
      } else {
        setCompletedScreenshots([]);
        console.debug('[MSModal] no cached screenshots found');
      }

      setIsCacheLoaded(true);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [isOpen, overlayOptions]);

  const availableOverlays = overlayOptions.filter(
    (overlay) => !completedScreenshots.some((c) => c.name === overlay.overlayName)
  );

  const currentOverlay = availableOverlays.length > 0 ? availableOverlays[0] : null;
  const totalOverlays = overlayOptions.length;
  const progress = totalOverlays > 0 ? (completedScreenshots.length / totalOverlays) * 100 : 0;
  const allCompleted = completedScreenshots.length === totalOverlays && totalOverlays > 0;

  // Fetch overlay data on change
  useEffect(() => {
    if (isOpen && isCacheLoaded && currentOverlay && searchAddress?.geometry?.rings) {
      console.debug('[MSModal] fetching overlay data for', currentOverlay);
      fetchOverlayData(currentOverlay.overlayId);
    }
  }, [isOpen, isCacheLoaded, currentOverlay, searchAddress]);

  const fetchOverlayData = async (overlayId) => {
    if (!searchAddress?.geometry?.rings) {
      setError('Address geometry not available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await getOverlayByAddressCoordinates({
        overlayId,
        // points: searchAddress.geometry.rings[0],
        geoJsonCode: geoJsonCode,
      });

      if (data) {
        console.debug('[MSModal] overlay data fetched', data);
        setCurrentOverlayData({ ...data, overlayId });
      } else {
        console.warn('[MSModal] overlay data fetch returned null/undefined');
        setError('Unable to fetch overlay data');
      }
    } catch (err) {
      console.error('[MSModal] fetchOverlayData error', err);
      setError('Failed to load overlay data');
    } finally {
      setLoading(false);
    }
  };

  // Handler for ArcgisMap to provide the capture function
  const handleScreenshotReady = (fn) => {
    console.debug('[MSModal] handleScreenshotReady called, fn available:', !!fn);
    captureScreenshotFn.current = fn;
  };

  // Robust capture that includes 1 retry on failure
  const captureScreenshot = async () => {
    if (!captureScreenshotFn.current || !currentOverlay || !currentOverlayData) {
      const msg = 'Map is not ready for screenshot';
      console.warn('[MSModal] captureScreenshot aborted:', msg, {
        hasFn: !!captureScreenshotFn.current,
        currentOverlay,
        currentOverlayData,
      });
      setError(msg);
      return;
    }

    // Prevent parallel captures
    if (isCapturing) {
      console.debug('[MSModal] capture already in progress, skipping');
      return;
    }

    setIsCapturing(true);
    setError('');

    // inner function to attempt capture/save once
    const attemptOnce = async () => {
      try {
        console.debug(
          '[MSModal] calling capture function for overlay:',
          currentOverlay.overlayName
        );
        const base64Screenshot = await captureScreenshotFn.current();

        if (!base64Screenshot) {
          throw new Error('capture function returned no data');
        }

        console.debug('[MSModal] screenshot captured, saving file...');
        const response = await saveFile({
          folderName: currentOverlay.overlayName,
          file: base64Screenshot,
        });

        if (!response || !response.documentId) {
          throw new Error('saveFile did not return documentId');
        }

        console.debug('[MSModal] saveFile success, documentId:', response.documentId);

        // cache single overlay screenshot
        cacheOverlaysScreenshots(currentOverlay.overlayName, response.documentId);

        const screenshotData = {
          name: currentOverlay.overlayName,
          screenshot: response.documentId,
        };

        // append to completed list safely
        setCompletedScreenshots((prev) => {
          const next = [...prev, screenshotData];
          console.debug('[MSModal] updated completedScreenshots', next);
          return next;
        });

        // cleanup current overlay state so effect can fetch next
        captureScreenshotFn.current = null;
        setCurrentOverlayData(null);
      } catch (err) {
        console.error('[MSModal] attemptOnce error', err);
        throw err;
      }
    };

    // Try first time, if fails try one more time
    try {
      await attemptOnce();
    } catch (err) {
      console.warn('[MSModal] first attempt failed, retrying once...', err);
      try {
        // small wait before retry
        await new Promise((r) => setTimeout(r, 700));
        await attemptOnce();
      } catch (err2) {
        console.error('[MSModal] retry failed', err2);
        setError('Failed to capture and save screenshot: ' + (err2?.message || 'Unknown'));
      }
    } finally {
      if (mountedRef.current) setIsCapturing(false);
    }
  };

  // 🔥 Polling + timeout mechanism:
  // Waits for captureScreenshotFn.current to be set, then calls captureScreenshot().
  // This is more robust than firing once with a short timeout; it will poll until either ready or timed out.
  useEffect(() => {
    if (!isOpen || allCompleted || !currentOverlayData) {
      // nothing to do
      return;
    }

    let intervalId = null;
    let attempts = 0;
    const maxAttempts = 30; // e.g., poll up to 30 times
    const intervalMs = 700; // poll every 700ms -> total ~21s max

    console.debug(
      `[MSModal] start polling for capture function (overlay=${currentOverlay?.overlayName})`
    );

    const startPolling = () => {
      intervalId = setInterval(async () => {
        attempts += 1;
        console.debug(`[MSModal] poll attempt ${attempts} / ${maxAttempts}`, {
          hasFn: !!captureScreenshotFn.current,
          isCapturing,
          isCompleting,
        });

        // If capture function present and not capturing, proceed
        if (captureScreenshotFn.current && !isCapturing && !isCompleting) {
          console.debug('[MSModal] captureFn found -> calling captureScreenshot()');
          clearInterval(intervalId);
          intervalId = null;
          try {
            await captureScreenshot();
          } catch (err) {
            console.error('[MSModal] captureScreenshot threw error:', err);
            // we continue; captureScreenshot handles its own retries
          }
          return;
        }

        // stop polling if we've tried too many times
        if (attempts >= maxAttempts) {
          console.warn('[MSModal] polling timed out — captureFn never appeared');
          clearInterval(intervalId);
          intervalId = null;
          if (mountedRef.current) {
            setError('Timed out waiting for map to become ready. Please try again.');
          }
        }
      }, intervalMs);
    };

    startPolling();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [isOpen, currentOverlayData, allCompleted]); // intentionally limited deps

  const handleManualCapture = async () => {
    if (!captureScreenshotFn.current) {
      setError('Map is not ready yet. Please wait...');
      return;
    }

    await captureScreenshot();
  };

  const handleComplete = async () => {
    if (completedScreenshots.length === 0) {
      setError('No screenshots captured');
      return;
    }

    setIsCompleting(true);
    setError('');

    try {
      console.debug('[MSModal] saving all screenshots', completedScreenshots.length);
      const res = await saveScreenshots({
        screenshotArray: completedScreenshots,
      });

      console.debug('[MSModal] saveScreenshots response', res);
      cacheOverlaysScreenshots(true, res.recordId);

      if (onComplete) onComplete(true, completedScreenshots);

      handleClose();
    } catch (err) {
      console.error('[MSModal] handleComplete failed', err);
      setError('Failed to save screenshots');
    } finally {
      if (mountedRef.current) setIsCompleting(false);
    }
  };

  const handleClose = () => {
    if (isCapturing || isCompleting) {
      console.debug('[MSModal] handleClose blocked while operation in progress');
      return;
    }

    console.debug('[MSModal] closing modal and cleaning up');
    setCurrentOverlayData(null);
    setCompletedScreenshots([]);
    setError('');
    setIsCacheLoaded(false);
    captureScreenshotFn.current = null;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Capture Overlay Screenshots</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isCapturing || isCompleting}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <MapContainer>
            {loading && <ModalLoadingScreen />}

            {!loading && currentOverlayData && !allCompleted && (
              <ArcgisMap
                address={searchAddress.address}
                cords={searchAddress.geometry?.rings}
                bushfireData={[currentOverlayData]}
                isMultiScreenshotMode={true}
                onScreenshotCaptured={handleScreenshotReady}
              />
            )}

            {allCompleted && (
              <CurrentOverlaySection>
                <OverlayLabel style={{ textAlign: 'center', marginBottom: '8px' }}>
                  All overlays captured automatically
                </OverlayLabel>
                <OverlayName style={{ textAlign: 'center' }}>
                  Click Complete to proceed.
                </OverlayName>
              </CurrentOverlaySection>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}
          </MapContainer>

          <Sidebar>
            <ProgressSection>
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              <ProgressText>
                {completedScreenshots.length} of {totalOverlays} completed
              </ProgressText>
            </ProgressSection>
            {currentOverlay && !allCompleted && (
              <CurrentOverlaySection>
                <OverlayLabel>Current Overlay</OverlayLabel>
                <OverlayName>{currentOverlay.overlayName}</OverlayName>
              </CurrentOverlaySection>
            )}
            {completedScreenshots.length > 0 && (
              <CompletedList>
                <OverlayLabel style={{ marginBottom: '12px' }}>Completed</OverlayLabel>
                {completedScreenshots.map((item, i) => (
                  <CompletedItem key={i}>
                    <CheckIcon size={20} />
                    <CompletedItemText>{item.name}</CompletedItemText>
                  </CompletedItem>
                ))}
              </CompletedList>
            )}

            <ButtonContainer>
              {!allCompleted && (
                <CaptureButton
                  onClick={handleManualCapture}
                  disabled={isCapturing || isCompleting || !currentOverlay || loading}
                >
                  {isCapturing ? 'Auto-capturing… Please wait' : 'Capture Manually'}
                </CaptureButton>
              )}
              {allCompleted && (
                <CompleteButton onClick={handleComplete} disabled={isCompleting}>
                  <Check size={18} />
                  {isCompleting ? 'Completing...' : 'Complete'}
                </CompleteButton>
              )}
            </ButtonContainer>
          </Sidebar>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default NewMultipleScreenshotModal;
